import { SnackbarAlert } from 'components/Custom'
import { createContext, useContext, useState, ReactNode } from 'react'

export type SnackbarOptions = {
  message: string
  statusType?: 'message' | 'success' | 'error'
  duration?: number | null
  hasCloseButton?: boolean
}

export type SnackbarContextType = {
  showAlert: (options: SnackbarOptions) => void
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined)

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [alertConfig, setAlertConfig] = useState<SnackbarOptions & { open: boolean }>({
    message: '',
    statusType: 'message',
    duration: 4000,
    open: false,
    hasCloseButton: true
  })

  const showAlert = (options: SnackbarOptions) => {
    setAlertConfig({
      ...options,
      open: true
    })
  }

  const handleClose = () => {
    setAlertConfig((prev) => ({ ...prev, open: false }))
  }

  return (
    <SnackbarContext.Provider value={{ showAlert }}>
      {children}
      {alertConfig.open && (
        <SnackbarAlert
          message={alertConfig.message}
          statusType={alertConfig.statusType}
          duration={alertConfig.duration}
          closeFunction={handleClose}
          hasCloseButton={alertConfig.hasCloseButton}
          open={alertConfig.open}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />
      )}
    </SnackbarContext.Provider>
  )
}

export const useSnackbarAlertContext = (): SnackbarContextType => {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error('useSnackbarAlertContext must be used within a SnackbarProvider')
  }
  return context
}
